import { TOOL_NAMES } from 'js/src/agent/toolNames';

/**
 * The timing guidance shared by the how-to skills, so voiceover, talking-head, and text videos
 * describe when an asset should appear in one voice. The narration variant (transcript words) is
 * used verbatim by the voiceover and talking-head skills; the text-video skill has no transcript
 * and keeps its own milliseconds framing, sharing only the group and icon rules below.
 */

/** How grouped children and paired icons are timed — the rules the tools enforce, for any vocabulary. */
export function groupedAssetTimingRules(param: 'start_words' | 'start_time_ms'): string {
    return `Grouped text (a \`GROUP\` with \`children\` in the video state — a list, or a heading with its
subtitle) follows two rules the tools enforce:
- Children reveal in order, top to bottom, and no child appears before its group. Time the
  children one at a time with \`${TOOL_NAMES.setText}\`, earlier items first; a child timed before
  an earlier sibling, or before its group, is rejected.
- Time the group itself with \`${TOOL_NAMES.setGroupAsset}\` only when the whole thing should land
  at once — it moves the group and every child together. Do that first if the group's own
  timing needs to move, then stagger the children after.

An icon that illustrates a text asset lands with it: give \`${TOOL_NAMES.setIcon}\` the same
\`${param}\` you gave the text. An icon showing from the first frame while its label waits reads
as a mistake.

The video state reports an asset's current timing as \`appearsAt\`. No \`appearsAt\` means the
asset is visible from the scene's first frame.`;
}

/** Timing text to spoken narration — voiceover and talking-head scenes alike. */
export function narrationTimingGuidance(): string {
    return `## Timing text to the narration
A text asset should land when the narration reaches the thing it says — not sit on screen
from the scene's first frame waiting to be talked about. Pass \`start_words\` to
\`${TOOL_NAMES.setText}\` as you write the text (it costs no extra call): consecutive transcript
tokens copied verbatim, in the same notation \`${TOOL_NAMES.addVoiceoverScene}\` takes (pause
tokens included), falling inside the scene's own \`covers\` range. The asset appears as the
first of those words is spoken and stays visible to the end of its scene. Prefer
\`start_words\` over \`start_time_ms\` on any scene with a transcript — it stays correct if the
narration is re-timed.

When to time, and when not to:
- A scene's headline — a first text asset that introduces the whole scene, or a lower third's
  name — belongs at the scene's start. Leave it untimed, even when the narration repeats its
  words later: a title that waits for its own words reads as a gap, not a reveal.
- A scene laid out as parallel items (two cards, icon-and-label pairs, a row of stats) has no
  headline: every item, the first included, lands as it is mentioned.
- Every later text asset is timed to the words it echoes: a list item lands on the word that
  names it, a stat lands as the number is said, a subtitle lands when its idea is reached.
  Start on the naming word itself, not the article, conjunction, or lead-in before it —
  \`["meat."]\`, not \`["and", "meat."]\`; \`["password"]\`, not \`["your", "password"]\`.
- Text that the narration never mentions still needs a home: time it to the moment it makes
  sense, or cut it.

${groupedAssetTimingRules('start_words')}`;
}
